/* eslint-disable react/jsx-no-comment-textnodes */
import { Box, Flex, HStack, IconButton, useDisclosure, Text, Drawer, DrawerBody, DrawerCloseButton, DrawerContent, DrawerHeader, DrawerOverlay } from '@chakra-ui/react';
import { AlignJustify } from 'lucide-react';
import { motion, useScroll, useSpring } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import HoverLink from '../components/HoverLink';
import React from 'react';

interface Props {
  children: React.ReactNode;
  href: string;
  onClose?: () => void;
}

const DesktopLinks = {
    '// Home': '#home',
    '// About': '#about',
    '// Skills': '#skills',
    '// Experience': '#experience',
    '// Portfolio': '#portfolio',
    '// News': '#news',
    '// Contact': '#contact',
};
  
const MobileLinks = {
    '// Home': '#home',
    '// About': '#about',
    '// Skills': '#skills',
    '// Experience': '#experience',
    '// Portfolio': '#portfolio',
    '// News': '#news',
    '// Contact': '#contact',
};

const MotionBox = motion(Box);

const NavLink = (props: Props) => {
  const { children, href, onClose } = props;
  const navigate = useNavigate();
  const location = useLocation();
  
  return (
    <MotionBox
        as="a"
        px={2}
        py={1}
        rounded={'md'}
        _hover={{
            textDecoration: 'none',
            color: 'white.100',
        }}
        href={href}
        fontFamily={"Source Code Pro Variable"}
        transitionDuration={"0.2s"}
        onClick={(event) => {
            event.preventDefault();
            if (onClose) {
                onClose();
            }
            
            const sectionId = href.replace('#', '');
            
            // Check if we're on the home page
            if (location.pathname === '/') {
                // We're on home page, just scroll to section
                const element = document.getElementById(sectionId);
                
                if (element) {
                    const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
                    const offsetPosition = elementPosition - 74;
                
                    window.scrollTo({
                        top: offsetPosition,
                        behavior: 'smooth'
                    });
                }
            } else {
                // We're on a different page, navigate to home first
                navigate('/');
                
                // Wait for navigation to complete, then scroll
                setTimeout(() => {
                    const element = document.getElementById(sectionId);
                    
                    if (element) {
                        const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
                        const offsetPosition = elementPosition - 74;
                    
                        window.scrollTo({
                            top: offsetPosition,
                            behavior: 'smooth'
                        });
                    }
                }, 100);
            }
        }}
    >
        {children}
    </MotionBox>
  );
};


export default function NavBar() {

    // Animated Progress Bar
    const { scrollYProgress } = useScroll();
    const scaleX = useSpring(scrollYProgress, {
        stiffness: 100,
        damping: 30,
        restDelta: 0.001,
    });
  
    const { isOpen, onOpen, onClose } = useDisclosure();
    const btnRef = React.useRef<HTMLButtonElement | null>(null);

    // Animation Variants for the Navbar containers
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.1,
            },
        },
    };

    // Animation Variants for the container items
    const itemVariants = {
        hidden: {
            opacity: 0, y: -50
        },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                type: "spring",
                stiffness: 500,
                damping: 36
            }
        },
    };
  
    return (
      <>
        <Box w={{ base: "100%", md: "80%" }} paddingTop={"10px"}>
            {/* Animated Progress Bar */}
            <motion.div
                style={{
                    scaleX,
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: 10,
                    background: '#5EEAD4',
                    transformOrigin: '0%',
                }}
            />
  
            <Flex h={16} alignItems={'center'} justifyContent={'space-between'}>
            {/* Open Menu Button */}
            <IconButton
                icon={<AlignJustify />}
                ref={btnRef}
                aria-label={'Open Menu'}
                display={{ md: 'none' }}
                onClick={isOpen ? onClose : onOpen}
                alignItems={'center'}
                justifyContent={'center'}
                padding={2}
                backgroundColor={"transparent"}
                color={"white.200"}
                position={"absolute"}
                left={8}
            />

            <Flex alignItems={'center'} width={{base: "100%", md: "auto"}} justifyContent={{base: "center", md: "flex-start"}}>
                <Box>
                <Text
                    fontSize={"xl"}
                    fontFamily={"Source Code Pro Variable"}
                    fontWeight={"600"}
                    textAlign={"left"}
                    color={"white.200"}
                >
                    <HoverLink text="// johnbajor.dev" link="/" color="#F9FAFB" isRainbow />
                </Text>
                </Box>
            </Flex>

            <HStack spacing={8} alignItems={'center'}>
                <motion.div
                    initial="hidden"
                    animate="visible"
                    variants={containerVariants}
                >
                <HStack
                    as={'nav'}
                    spacing={4}
                    display={{ base: 'none', md: 'flex' }}
                    color={"white.300"}
                    fontFamily={"Source Code Pro Variable"}
                >
                    {Object.entries(DesktopLinks).map(([text, href]) => (
                    <motion.div key={text} variants={itemVariants}>
                        <NavLink href={href}>{text}</NavLink>
                    </motion.div>
                    ))}
                </HStack>
                </motion.div>
            </HStack>
        </Flex>
  
            <Drawer isOpen={isOpen} placement='left' onClose={onClose} finalFocusRef={btnRef}>

                <DrawerOverlay />

                <DrawerContent backgroundColor={"white.200"}>

                    <DrawerCloseButton />

                    <DrawerHeader>
                        <Text fontFamily={"Source Code Pro Variable"} fontWeight="600" textAlign={"left"} color={"black.200"}>
                        // johnbajor.dev
                        </Text>
                    </DrawerHeader>

                    <DrawerBody>
                      <Flex flexDirection="column" gap={4}>
                          {Object.entries(MobileLinks).map(([text, href]) => (
                              <NavLink key={text} href={href} onClose={onClose}>{text}</NavLink>
                          ))}
                          <Text fontFamily={"Source Code Pro Variable"} fontWeight="600" textAlign={"left"} color={"black.200"}>
                          © 2026.
                          </Text>
                          <Text fontFamily={"Source Code Pro Variable"} fontWeight="600" textAlign={"left"} color={"black.200"}>
                          Made with sticks, duct tape, and WD-40 by John Bajor.
                          </Text>
                          <Text fontFamily={"Source Code Pro Variable"} fontWeight="600" textAlign={"left"} color={"black.200"}>
                          All rights reserved.
                          </Text>
                      </Flex>
                  </DrawerBody>

                </DrawerContent>

            </Drawer>

        </Box>
      </>
    );
}
